import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type SimSpeed = 0 | 1 | 4 | 16 | 30;

export interface FiredEventRecord {
  eventId: string;
  firedAt: string; // ISO real-world time
  simTime: string; // ISO sim time
}

const DEFAULT_START =
  (import.meta.env.VITE_SIM_START_ISO as string | undefined) ?? '2026-05-04T13:00:00+10:00';

export interface SimulationState {
  clockIso: string;
  speed: SimSpeed;
  running: boolean;
  startedAt: string | null;
  firedEvents: FiredEventRecord[];
  scenarioCursor: number | null;
  setClock: (iso: string) => void;
  setSpeed: (speed: SimSpeed) => void;
  setRunning: (running: boolean) => void;
  reset: () => void;
  recordFired: (eventId: string) => void;
  setScenarioCursor: (idx: number | null) => void;
}

export const useSimulationStore = create<SimulationState>()(
  persist(
    (set) => ({
      clockIso: DEFAULT_START,
      speed: 1,
      running: false,
      startedAt: null,
      firedEvents: [],
      scenarioCursor: null,
      setClock: (iso) => set({ clockIso: iso }),
      setSpeed: (speed) => set({ speed }),
      setRunning: (running) =>
        set({ running, startedAt: running ? new Date().toISOString() : null }),
      reset: () =>
        set({
          clockIso: DEFAULT_START,
          speed: 1,
          running: false,
          startedAt: null,
          firedEvents: [],
          scenarioCursor: null,
        }),
      recordFired: (eventId) =>
        set((s) => ({
          firedEvents: [
            ...s.firedEvents,
            { eventId, firedAt: new Date().toISOString(), simTime: s.clockIso },
          ],
        })),
      setScenarioCursor: (idx) => set({ scenarioCursor: idx }),
    }),
    {
      name: 'ati-simulation',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (s) => ({
        clockIso: s.clockIso,
        speed: s.speed,
        firedEvents: s.firedEvents,
        scenarioCursor: s.scenarioCursor,
      }),
    },
  ),
);

export const SIM_START_ISO = DEFAULT_START;
